// Panel Comisiones ML — agregación mensual de comisión + costo fijo + IVA sobre comisión
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

function fmt(n) { return `$ ${Math.round(Number(n || 0)).toLocaleString('es-UY')}` }
function pctFmt(a, b) { if (!b) return '—'; return `${(a/b*100).toFixed(1)}%` }
function mesLabel(ym) {
  const [y, m] = ym.split('-')
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-UY', { month: 'long', year: 'numeric' })
}

export default function ComisionesML() {
  const [ventas, setVentas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadData() }, [])
  async function loadData() {
    setLoading(true)
    const desde = new Date(); desde.setMonth(desde.getMonth() - 6); desde.setDate(1)
    const { data } = await supabase.from('ml_ventas')
      .select('id, ml_order_id, date_created, date_paid, monto_bruto, comision_monto, costo_fijo, iva_debito, neto, status')
      .in('status', ['paid', 'shipped', 'delivered'])
      .gte('date_created', desde.toISOString())
      .order('date_created', { ascending: false })
    setVentas(data || [])
    setLoading(false)
  }

  const porMes = useMemo(() => {
    const map = new Map()
    for (const v of ventas) {
      if (!v.date_created) continue
      const d = new Date(v.date_created)
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const row = map.get(ym) || { ym, ventas: 0, bruto: 0, comision: 0, costoFijo: 0, ivaSobreComision: 0, neto: 0 }
      row.ventas += 1
      row.bruto += Number(v.monto_bruto || 0)
      row.comision += Number(v.comision_monto || 0)
      row.costoFijo += Number(v.costo_fijo || 0)
      row.ivaSobreComision += Number(v.iva_debito || 0)
      row.neto += Number(v.neto || 0)
      map.set(ym, row)
    }
    return Array.from(map.values()).sort((a, b) => b.ym.localeCompare(a.ym))
  }, [ventas])

  const total = useMemo(() => {
    return porMes.reduce((acc, r) => ({
      ventas: acc.ventas + r.ventas,
      bruto: acc.bruto + r.bruto,
      comision: acc.comision + r.comision,
      costoFijo: acc.costoFijo + r.costoFijo,
      ivaSobreComision: acc.ivaSobreComision + r.ivaSobreComision,
      neto: acc.neto + r.neto,
    }), { ventas: 0, bruto: 0, comision: 0, costoFijo: 0, ivaSobreComision: 0, neto: 0 })
  }, [porMes])

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, color: '#1A3461', margin: 0 }}>Comisiones ML</h1>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>Comisión 13,5% + costo fijo + IVA débito · agregado por mes</div>
          </div>
          <button onClick={loadData} style={{ padding: '7px 14px', background: '#1A3461', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>↻ Actualizar</button>
        </div>

        {/* Totales acumulados */}
        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: 14, marginBottom: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
          <div><div style={{ fontSize: 10.5, color: '#0369A1', fontWeight: 700, letterSpacing: '.05em' }}>ÚLTIMOS 6 MESES</div><div style={{ fontSize: 22, color: '#0C4A6E', fontWeight: 800 }}>{total.ventas} ventas</div></div>
          <div><div style={{ fontSize: 10.5, color: '#0369A1', fontWeight: 700 }}>BRUTO VENDIDO</div><div style={{ fontSize: 20, color: '#1A3461', fontWeight: 800 }}>{fmt(total.bruto)}</div></div>
          <div><div style={{ fontSize: 10.5, color: '#0369A1', fontWeight: 700 }}>COMISIÓN 511310</div><div style={{ fontSize: 20, color: '#DC2626', fontWeight: 800 }}>−{fmt(total.comision)}</div><div style={{ fontSize: 10.5, color: '#94A3B8' }}>{pctFmt(total.comision, total.bruto)} del bruto</div></div>
          <div><div style={{ fontSize: 10.5, color: '#0369A1', fontWeight: 700 }}>COSTO FIJO 511311</div><div style={{ fontSize: 20, color: '#DC2626', fontWeight: 800 }}>−{fmt(total.costoFijo)}</div></div>
          <div><div style={{ fontSize: 10.5, color: '#0369A1', fontWeight: 700 }}>NETO ML</div><div style={{ fontSize: 20, color: '#10B981', fontWeight: 800 }}>{fmt(total.neto)}</div><div style={{ fontSize: 10.5, color: '#94A3B8' }}>{pctFmt(total.neto, total.bruto)} del bruto</div></div>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Cargando…</div> :
         porMes.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', background: '#F8FAFC', borderRadius: 8 }}>
            Todavía no hay ventas ML pagadas en los últimos 6 meses. Cuando entren aparecen agregadas por mes.
          </div>
         ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
            <thead style={{ background: '#F8FAFC', color: '#475569' }}>
              <tr>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Mes</th>
                <th style={{ textAlign: 'right' }}>Ventas</th>
                <th style={{ textAlign: 'right' }}>Bruto</th>
                <th style={{ textAlign: 'right' }}>Comisión</th>
                <th style={{ textAlign: 'right' }}>Costo fijo</th>
                <th style={{ textAlign: 'right' }}>IVA débito</th>
                <th style={{ textAlign: 'right' }}>Neto ML</th>
                <th style={{ textAlign: 'right' }}>% comisión</th>
              </tr>
            </thead>
            <tbody>
              {porMes.map(r => (
                <tr key={r.ym} style={{ borderTop: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1A3461', textTransform: 'capitalize' }}>{mesLabel(r.ym)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.ventas}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.bruto)}</td>
                  <td style={{ textAlign: 'right', color: '#DC2626' }}>−{fmt(r.comision)}</td>
                  <td style={{ textAlign: 'right', color: '#DC2626' }}>−{fmt(r.costoFijo)}</td>
                  <td style={{ textAlign: 'right', color: '#94A3B8' }}>{fmt(r.ivaSobreComision)}</td>
                  <td style={{ textAlign: 'right', color: '#10B981', fontWeight: 700 }}>{fmt(r.neto)}</td>
                  <td style={{ textAlign: 'right', color: '#64748B' }}>{pctFmt(r.comision, r.bruto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
         )}

        <div style={{ marginTop: 20, padding: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12, color: '#78350F' }}>
          <strong>Códigos Zeta que refleja este canal:</strong> <code>511310</code> Comisiones ML · <code>511311</code> Costo Fijo ML · <code>1132104</code> ML por cobrar UYU. Los descuentos van a <code>512109</code>. Confirmado por Roberto el 1-jul-2026.
        </div>
      </div>
    </AdminLayout>
  )
}
